import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { get_course_all_year_data } from "../../../api/common";
import { get_active_class_terms, class_add } from "../../../api/Class";

// Bulk-duplicate existing classes into another term (and/or year).
// Only the class shell is copied — students are never carried over, because
// enrolling a student is a paid, opt-in action.
//
// Everything else (course, grade, timing, instructor, duration, max week)
// is inherited as-is; the class name has its term marker rewritten.

// "Y10 T3 English & Writing" -> "Y10 T4 English & Writing"
export const renameForTerm = (name, fromTermNo, toTermNo) => {
  if (!name) return name;
  if (!fromTermNo || !toTermNo || String(fromTermNo) === String(toTermNo)) return name;
  // Replace a standalone T<n> token (T3, t3) but leave things like "T3X" alone.
  const re = new RegExp(`\\bT${fromTermNo}\\b`, "gi");
  if (re.test(name)) return name.replace(re, `T${toTermNo}`);
  return name;
};

// /api/courses-total-year returns rows shaped { current_year: 2026 }.
// The other year dropdowns in this folder read the same field.
const yearOf = (y) => y?.current_year ?? y?.year ?? y?.id;

// Term names come back as "1 (NSW) " — everywhere we need the term *number*
// (labels, and the T1 -> T2 rename) we want just the leading digits.
const termNoOf = (name) => String(name ?? "").trim().match(/^\d+/)?.[0] || "";

const DuplicateClasses = ({ classes, branchId, onClose, onDone }) => {
  const [yearData, setYearData] = useState([]);
  const [fromYear, setFromYear] = useState("");
  const [fromTerm, setFromTerm] = useState("");
  const [toYear, setToYear] = useState("");
  const [toTerm, setToTerm] = useState("");
  const [terms, setTerms] = useState([]);
  const [toTerms, setToTerms] = useState([]);
  const [selected, setSelected] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [busy, setBusy] = useState(false);

  const branchState = (() => {
    try {
      return JSON.parse(localStorage.getItem("userData"))?.data?.branchstate;
    } catch {
      return undefined;
    }
  })();

  useEffect(() => {
    get_course_all_year_data().then((r) => setYearData(r?.data || []));
  }, []);

  useEffect(() => {
    if (!fromYear) return setTerms([]);
    get_active_class_terms(fromYear, branchState).then((r) => setTerms(r?.data || []));
  }, [fromYear, branchState]);

  useEffect(() => {
    if (!toYear) return setToTerms([]);
    get_active_class_terms(toYear, branchState).then((r) => setToTerms(r?.data || []));
  }, [toYear, branchState]);

  // Classes that belong to the chosen source year + term.
  const sourceClasses = useMemo(() => {
    if (!fromYear || !fromTerm) return [];
    return (classes || []).filter(
      (c) => String(c.year) === String(fromYear) && String(c.term_id) === String(fromTerm)
    );
  }, [classes, fromYear, fromTerm]);

  // Select everything by default whenever the source changes.
  useEffect(() => {
    setSelected(sourceClasses.map((c) => c.id));
  }, [sourceClasses]);

  const termNo = (list, id) => termNoOf(list.find((t) => String(t.id) === String(id))?.name);
  const fromTermNo = termNo(terms, fromTerm);
  const toTermNo = termNo(toTerms, toTerm);

  // Names that already exist in the target term — skip those.
  const existingInTarget = useMemo(() => {
    if (!toYear || !toTerm) return new Set();
    return new Set(
      (classes || [])
        .filter((c) => String(c.year) === String(toYear) && String(c.term_id) === String(toTerm))
        .map((c) => String(c.class_name || "").trim().toLowerCase())
    );
  }, [classes, toYear, toTerm]);

  const rows = sourceClasses.map((c) => {
    const newName = renameForTerm(c.class_name, fromTermNo, toTermNo);
    return {
      ...c,
      newName,
      duplicate: existingInTarget.has(String(newName).trim().toLowerCase()),
    };
  });

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const canRun =
    fromYear && fromTerm && toYear && toTerm && selected.length > 0 &&
    !(String(fromYear) === String(toYear) && String(fromTerm) === String(toTerm));

  const run = async () => {
    const picked = rows.filter((r) => selected.includes(r.id) && !r.duplicate);
    if (picked.length === 0) {
      toast.error("Nothing to duplicate — the selected classes already exist in that term.");
      return;
    }
    setBusy(true);
    let ok = 0;
    let failed = 0;
    for (const c of picked) {
      const payload = {
        branch_id: branchId,
        class_name: c.newName,
        year: toYear,
        term: toTerm,
        grade: c.grade_id,
        course_id: c.course_id,
        class_timing: c.class_timing || "",
        class_description: c.class_description || "",
        instructor_name: c.instructor_name || "",
        start_date: startDate || c.start_date || "",
        max_week: c.max_week || "",
        start_time: c.start_time || "",
        duration_minutes: c.duration_minutes || "",
      };
      const res = await class_add(payload);
      res?.status ? ok++ : failed++;
    }
    setBusy(false);
    const skipped = selected.length - picked.length;
    toast.success(
      `Duplicated ${ok} class${ok === 1 ? "" : "es"}` +
        (skipped ? `, skipped ${skipped} already there` : "") +
        (failed ? `, ${failed} failed` : "")
    );
    onDone();
  };

  const dupCount = rows.filter((r) => selected.includes(r.id) && r.duplicate).length;

  return (
    <div style={ov} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={head}>
          <h5 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#1f2937" }}>
            Duplicate classes to another term
          </h5>
          <button type="button" onClick={onClose} style={x}>×</button>
        </div>

        <p style={hint}>
          Copies the class setup only — course, grade, time, instructor and duration carry over.
          Students are not copied.
        </p>

        {/* From / To */}
        <div style={grid2}>
          <div>
            <div style={legend}>Copy from</div>
            <div style={row2}>
              <select style={inp} value={fromYear} onChange={(e) => { setFromYear(e.target.value); setFromTerm(""); }}>
                <option value="">Year</option>
                {yearData.map((y) => (
                  <option key={yearOf(y)} value={yearOf(y)}>{yearOf(y)}</option>
                ))}
              </select>
              <select style={inp} value={fromTerm} onChange={(e) => setFromTerm(e.target.value)} disabled={!fromYear}>
                <option value="">Term</option>
                {terms.map((t) => (
                  <option key={t.id} value={t.id}>Term {termNoOf(t.name)}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <div style={legend}>Copy to</div>
            <div style={row2}>
              <select style={inp} value={toYear} onChange={(e) => { setToYear(e.target.value); setToTerm(""); }}>
                <option value="">Year</option>
                {yearData.map((y) => (
                  <option key={yearOf(y)} value={yearOf(y)}>{yearOf(y)}</option>
                ))}
              </select>
              <select style={inp} value={toTerm} onChange={(e) => setToTerm(e.target.value)} disabled={!toYear}>
                <option value="">Term</option>
                {toTerms.map((t) => (
                  <option key={t.id} value={t.id}>Term {termNoOf(t.name)}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={legend}>Start date for the new classes <span style={{ fontWeight: 400, color: "#94a3b8" }}>(optional)</span></div>
          <input type="date" style={{ ...inp, maxWidth: 220 }} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>

        {/* Class picker */}
        <div style={{ marginTop: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={legend}>
              Classes to duplicate
              {rows.length > 0 && (
                <span style={{ fontWeight: 400, color: "#94a3b8" }}> · {selected.length} of {rows.length} selected</span>
              )}
            </div>
            {rows.length > 0 && (
              <button type="button" style={linkBtn}
                onClick={() => setSelected(selected.length === rows.length ? [] : rows.map((r) => r.id))}>
                {selected.length === rows.length ? "Clear all" : "Select all"}
              </button>
            )}
          </div>

          <div style={listBox}>
            {!fromYear || !fromTerm ? (
              <div style={empty}>Choose the year and term to copy from.</div>
            ) : rows.length === 0 ? (
              <div style={empty}>No classes found in that term.</div>
            ) : (
              rows.map((r) => (
                <label key={r.id} style={{ ...item, opacity: r.duplicate ? 0.55 : 1 }}>
                  <input
                    type="checkbox"
                    checked={selected.includes(r.id)}
                    onChange={() => toggle(r.id)}
                    disabled={r.duplicate}
                  />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 13, color: "#1e293b" }}>{r.class_name}</span>
                    {toTermNo && r.newName !== r.class_name && (
                      <span style={{ fontSize: 12, color: "#64748b" }}> → <b style={{ color: "#0f172a" }}>{r.newName}</b></span>
                    )}
                  </span>
                  {r.duplicate && <span style={pill}>already exists</span>}
                </label>
              ))
            )}
          </div>
          {dupCount > 0 && (
            <p style={{ ...hint, marginTop: 6 }}>
              {dupCount} class{dupCount === 1 ? "" : "es"} already exist in that term and will be skipped.
            </p>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
          <button type="button" style={btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" style={{ ...btnPrimary, opacity: canRun && !busy ? 1 : 0.5 }}
            onClick={run} disabled={!canRun || busy}>
            {busy ? "Duplicating…" : "Duplicate"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ov = {
  position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
  display: "flex", alignItems: "center", justifyContent: "center",
  zIndex: 1050, padding: 24, overflowY: "auto",
};
const card = {
  background: "#fff", borderRadius: 10, maxWidth: 680, width: "100%",
  padding: "20px 24px", boxShadow: "0 20px 50px rgba(15,23,42,0.25)",
  maxHeight: "calc(100vh - 48px)", overflowY: "auto",
};
const head = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 };
const x = { background: "transparent", border: "none", fontSize: 22, color: "#94a3b8", cursor: "pointer", padding: 0 };
const hint = { fontSize: 12, color: "#64748b", margin: "0 0 14px" };
const legend = { fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 };
const grid2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 };
const row2 = { display: "flex", gap: 8 };
const inp = {
  fontSize: 13, padding: "7px 10px", borderRadius: 6,
  border: "1px solid #d1d5db", background: "#fff", width: "100%",
};
const listBox = {
  border: "1px solid #e5e7eb", borderRadius: 8, maxHeight: 240,
  overflowY: "auto", background: "#fff",
};
const item = {
  display: "flex", alignItems: "center", gap: 10,
  padding: "9px 12px", borderBottom: "1px solid #f1f5f9", cursor: "pointer",
};
const empty = { padding: 20, fontSize: 13, color: "#94a3b8", textAlign: "center" };
const pill = {
  fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.05,
  background: "#f1f5f9", color: "#64748b", padding: "2px 8px", borderRadius: 999, whiteSpace: "nowrap",
};
const linkBtn = {
  background: "none", border: "none", color: "#3b82f6",
  fontSize: 12, cursor: "pointer", padding: 0,
};
const btnGhost = {
  padding: "7px 16px", borderRadius: 6, fontSize: 13,
  background: "#e5e7eb", color: "#374151", border: "none", cursor: "pointer",
};
const btnPrimary = {
  padding: "7px 18px", borderRadius: 6, fontSize: 13, fontWeight: 500,
  background: "#3b82f6", color: "#fff", border: "none", cursor: "pointer",
};

export default DuplicateClasses;
